import { Card, CardStandardHeader, CoherenceDataGrid, CoherencePanel } from "@coherence-design-system/controls";
import { FontSizes, FontWeights, ActionButton, TextField } from "@fluentui/react";
import { Stack, IStackTokens, IStackProps } from '@fluentui/react/lib/Stack';
import { useBoolean } from '@fluentui/react-hooks';
import React, { useEffect, useState } from "react";
import { CustomerAccountDetail } from "../../models/Accounts/IAccountDetail";
import "./Account.css";
import { AccountDetailService } from "../../services/Accounts/AccountDetailService";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

const stackTokens: Partial<IStackTokens> = { childrenGap: 20 };


interface IAccountDetailState {
    loading: boolean,
    accountDetails: CustomerAccountDetail,
    errorMsg: string
}

export const AccountDetail: React.FunctionComponent = () => {

    const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
    const [isBillingPanelOpen, { setTrue: openBillingPanel, setFalse: dismissBillingPanel }] = useBoolean(false);
    const [isBuyerPanelOpen, { setTrue: openBuyerPanel, setFalse: dismissBuyerPanel }] = useBoolean(false);

    const columnProps: Partial<IStackProps> = {
        tokens: { childrenGap: 5 },
        styles: { root: { width: 200 } },
    };

    const appInsights = useAppInsightsContext();

    const [textFieldBillToValue, setTextFieldBillToValue] = useState<string>('');
    const [textFieldShipToValue, setTextFieldShipToValue] = useState<string>('');
    const [aDetail, setDetail] = useState<IAccountDetailState>({
        loading: false,
        accountDetails: {} as CustomerAccountDetail,
        errorMsg: ''
    })

    useEffect(() => {
        appInsights.trackTrace({message: "API call started: AccountDetailService -> GetAccountDetail() in AccountDetail.tsx"});
        AccountDetailService.getAccountDetail('bfe0ef5a-bd3b-4486-99ce-72d0fbcf35c2')
            .then((res) => {
                if (res !== undefined) {
                    setDetail({
                        ...aDetail,
                        loading: false,
                        accountDetails: res.data.data
                    })
                }
            })
            .catch((err) => {
                appInsights.trackException({error: err, properties: {name: "In AccountDetail.tsx, error while retrieving account details."}})
                setDetail({
                    ...aDetail,
                    loading: false,
                    errorMsg: err.message
                })
            });
            appInsights.trackTrace({message: "API call ended: AccountDetailService -> GetAccountDetail() in AccountDetail.tsx"});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { accountDetails } = aDetail;

    const [isBillToEditable, setBillToEditable] = useState(false);


    const handleEditBillTo = () => {
        appInsights.trackEvent({name:"In AccountDetail.tsx, Clicked on Edit button of Bill To Address."});
        setTextFieldBillToValue(accountDetails.billTo.address);
        setBillToEditable(!isBillToEditable);
    };

    const handleCancelBillTo = () => {
        appInsights.trackEvent({name:"In AccountDetail.tsx, Clicked on Cancel button of Bill To Address."});
        accountDetails.billTo.address = textFieldBillToValue;
        setBillToEditable(!isBillToEditable);
    };


    const handleBillToTextFieldChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {

        console.log(newValue);

        setDetail(prevState => ({
            ...prevState,
            accountDetails: {
                ...prevState.accountDetails,
                billTo: {
                    ...prevState.accountDetails.billTo,
                    address: newValue || ''
                }
            }
        }));
    }

    const handleEditBillToSave = () => {
        appInsights.trackEvent({name:"In AccountDetail.tsx, Clicked on Save button of Bill To Address."});
        AccountDetailService.CustomerAccountUpdate(accountDetails);
        setBillToEditable(!isBillToEditable);
    };


    const [isShipToEditable, setShipToEditable] = useState(false);

    const handleEditShipTo = () => {
        appInsights.trackEvent({name:"In AccountDetail.tsx, Clicked on Edit button of Ship To Address."});
        setTextFieldShipToValue(accountDetails.shipTo.address);
        setShipToEditable(!isShipToEditable);
    };

    const handleCancelShipTo = () => {
        appInsights.trackEvent({name:"In AccountDetail.tsx, Clicked on Cancel button of Ship To Address."});
        accountDetails.shipTo.address = textFieldShipToValue;
        setShipToEditable(!isShipToEditable);
    };


    const handleShipToTextFieldChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {

        console.log(newValue);
        
        setDetail(prevState => ({
            ...prevState,
            accountDetails: {
                ...prevState.accountDetails,
                shipTo: {
                    ...prevState.accountDetails.shipTo,
                    address: newValue || ''
                }
            }
        }));
    }

    const handleEditShipToSave = () => {
        appInsights.trackEvent({name:"In AccountDetail.tsx, Clicked on Save button of Ship To Address."});
        AccountDetailService.CustomerAccountUpdate(accountDetails);
        setShipToEditable(!isShipToEditable);
    };


    return (
        <Stack
            tokens={stackTokens}>
            <div
                style={{
                    width: '600px',
                    marginLeft: '100px',
                    marginTop: '10px'
                }}
            >
                <p style={{ fontWeight: FontWeights.bold, fontSize: FontSizes.large, marginBottom: '10px' }}>Billing Profiles</p>
                <Card
                    //footer={<CardFooter links={[{ children: <span>View details</span>, href: "/AccountPanel" }]} />}
                    header={<CardStandardHeader cardTitle={accountDetails?.billingAccount} />}>
                    <Stack horizontal>
                        <Stack {...columnProps}>
                            <span><b>Billing profile owner</b></span>
                            <span>{accountDetails.billingProfileOwners?.[0].name}</span>
                            <br />
                            <span><b>Authorizwed buyers</b></span>
                            <span>{accountDetails.authorizedBuyers?.[0].name}</span>
                        </Stack>

                        <Stack {...columnProps}>
                            <span><b>Lifetime orders</b></span>
                            <span>{accountDetails?.totalOrders}</span>
                            <br />
                            <span><b>Payment method</b></span>
                            <span>{accountDetails?.paymentMethod}</span>
                        </Stack>
                        <Stack {...columnProps}>
                            <span><b>Bill to</b></span>
                            <span>{accountDetails?.billTo?.companyName}</span>
                            <span>{accountDetails?.billTo?.address}</span>
                            <br />
                            <span><b>Ship to</b></span>
                            <span>{accountDetails?.shipTo?.companyName}</span>
                            <span>{accountDetails?.shipTo?.address}</span>
                        </Stack>


                    </Stack>
                    <Stack horizontal>
                        <div>
                            <ActionButton onClick={openPanel}>View Details</ActionButton>
                            <CoherencePanel
                                panelSize="500px"
                                headerText="Sample panel"
                                isOpen={isOpen}
                                onDismiss={dismissPanel}
                                // You MUST provide this prop! Otherwise screen readers will just say "button" with no label.
                                closeButtonAriaLabel="Close"
                            >
                                <p style={{ fontSize: FontSizes.large, fontWeight: FontWeights.semibold, marginBottom: '10px' }}>{accountDetails?.billingAccount}</p>
                                <Stack {...columnProps}>
                                    <span><b>Bill to</b></span>
                                    {!isBillToEditable &&
                                        <>
                                            <span>{accountDetails?.billTo?.address}</span>
                                            <ActionButton onClick={handleEditBillTo}>Edit</ActionButton>
                                        </>
                                    }
                                    {isBillToEditable &&
                                        <>
                                            <TextField
                                                name="address"
                                                value={accountDetails?.billTo?.address}
                                                onChange={handleBillToTextFieldChange}
                                                multiline autoAdjustHeight
                                            />
                                            <div>
                                                <ActionButton onClick={handleEditBillToSave}>Save</ActionButton>
                                                <ActionButton onClick={handleCancelBillTo}>Cancel</ActionButton>
                                            </div>
                                        </>
                                    }

                                    <br />
                                    <span><b>Ship to</b></span>
                                    {!isShipToEditable &&
                                        <>
                                            <span>{accountDetails?.shipTo?.address}</span>
                                            <ActionButton onClick={handleEditShipTo}>Edit</ActionButton>
                                        </>
                                    }
                                    {isShipToEditable &&
                                        <>
                                            <TextField
                                                name="address"
                                                value={accountDetails?.shipTo?.address}
                                                onChange={handleShipToTextFieldChange}
                                                multiline autoAdjustHeight
                                            />
                                            <div>
                                                <ActionButton onClick={handleEditShipToSave}>Save</ActionButton>
                                                <ActionButton onClick={handleCancelShipTo}>Cancel</ActionButton>
                                            </div>
                                        </>
                                    }

                                    <br />
                                    <span><b>Payment method</b></span>
                                    <span>{accountDetails?.paymentMethod}</span>

                                    <br />
                                    <span><b>Lifetime orders</b></span>
                                    <span>{accountDetails?.totalOrders}</span>

                                    <br />
                                    <span><b>Billing profile owners</b></span>
                                    <CoherenceDataGrid
                                        data={accountDetails.billingProfileOwners && accountDetails.billingProfileOwners}
                                        listConfig={[
                                            {
                                                fieldName: 'name',
                                                key: 'name',
                                                name: 'Name',
                                                type: 'string'
                                            },
                                            {
                                                fieldName: 'phoneNumber',
                                                key: 'phoneNumber',
                                                name: 'Phone Number',
                                                type: 'string'
                                            },
                                            {
                                                fieldName: 'email',
                                                key: 'email',
                                                name: 'Email',
                                                type: 'string'
                                            }
                                        ]}
                                    />

                                    <ActionButton>Add profile owner</ActionButton>
                                    <br />
                                    <span><b>Authorized buyers</b></span>
                                    <CoherenceDataGrid
                                        data={accountDetails.authorizedBuyers && accountDetails.authorizedBuyers}
                                        listConfig={[
                                            {
                                                fieldName: 'name',
                                                key: 'name',
                                                name: 'Name',
                                                type: 'string'
                                            },
                                            {
                                                fieldName: 'phoneNumber',
                                                key: 'phoneNumber',
                                                name: 'Phone Number',
                                                type: 'string'
                                            },
                                            {
                                                fieldName: 'email',
                                                key: 'email',
                                                name: 'Email',
                                                type: 'string',
                                                isResizable: true
                                            }
                                        ]}
                                    />
                                    <ActionButton>Add authorized buyers</ActionButton>
                                </Stack>
                            </CoherencePanel>
                        </div>
                    </Stack>
                </Card>
            </div>
        </Stack >
    );
}